/* import __COLOCATED_TEMPLATE__ from './modal.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type PlaygroundApiService from 'embercom/services/playground-api';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'embercom/services/intl';
import {
  ExtractionType,
  type PlaygroundQuestionGeneration,
} from 'embercom/lib/fin-playground/question-generation';
import { TOPICS_EXPLORER_HELP_URL } from 'embercom/lib/fin-playground/topics';
import { TrackedArray } from 'tracked-built-ins';
import { ResponseError } from 'embercom/lib/inbox/requests';
import { captureException } from 'embercom/lib/sentry';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import { type SafeString } from '@ember/template/-private/handlebars';
import escapeRegExp from 'embercom/lib/escape-regular-expression';

interface Args {
  onClose: () => void;
  questionLimit: number;
  isQuestionGenerationPossible: boolean;
  isQuestionGenerationRunningForApp: boolean;
  questionGeneration?: PlaygroundQuestionGeneration;
  preSelectedQuestions?: string[];
  showExceedingQuestionLimitNotification: () => void;
}

class QuestionInput {
  @tracked text = '';

  constructor(text = '') {
    this.text = text;
  }
}

export type QuestionAdditionMethod = 'manual' | 'csv' | 'question-generation';
type ConversationSource = 'all' | 'by-topic';

export default class Modal extends Component<Args> {
  @service declare playgroundApi: PlaygroundApiService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: $TSFixMe;

  @tracked groupName = '';
  @tracked questionAdditionMethod: QuestionAdditionMethod = this.getInitialQuestionAdditionMethod();
  @tracked manuallyAddedQuestions: TrackedArray<QuestionInput> = new TrackedArray(
    this.args.preSelectedQuestions && this.args.preSelectedQuestions.length > 0
      ? this.args.preSelectedQuestions.map((text: string) => new QuestionInput(text))
      : [new QuestionInput()],
  );
  @tracked file = null;
  @tracked fileIsValid = false;
  @tracked fileFailedToUpload = false;
  @tracked parsingErrorMessage = '';
  @tracked questionsFromCSV: string[] = [];
  @tracked conversationSource: ConversationSource = 'all';
  @tracked selectedTopic: string | undefined = undefined;

  get selectedTopicName() {
    if (!this.selectedTopic) {
      return null;
    }

    // Find the selected topic within the entities structure
    let topics = this.args.questionGeneration?.availableTopics ?? [];
    let selectedItem = topics.find((topic) => topic.value === this.selectedTopic);
    return selectedItem?.text ?? null;
  }

  get showQuestionGenerationMethod() {
    return this.appService.app.canUseFinPlaygroundTopics || this.args.isQuestionGenerationPossible;
  }

  get shouldShowSyncMessage(): boolean {
    return this.playgroundApi.hasDisconnectedZendeskOrSalesforceStandalone;
  }

  get shouldShowUnsupportedMessage(): boolean {
    return this.playgroundApi.hasUnsupportedStandalonePlatforms;
  }

  get questionGenerationMethodLabel(): string {
    if (this.appService.app.canUseStandalone) {
      return this.intl.t(
        'ai-agent.playground.new-group-modal.add-questions.methods.question-generation-standalone',
      );
    }
    return this.intl.t(
      'ai-agent.playground.new-group-modal.add-questions.methods.question-generation',
    );
  }

  getInitialQuestionAdditionMethod(): QuestionAdditionMethod {
    if (this.args.preSelectedQuestions && this.args.preSelectedQuestions.length > 0) {
      return 'manual';
    }
    return this.showQuestionGenerationMethod ? 'question-generation' : 'manual';
  }

  get hasTopics() {
    return this.args.questionGeneration?.hasTopics ?? false;
  }

  @action
  trackByTopicOptionRender() {
    let hasTopics = this.hasTopics;

    // Track when "By topic" option is visible in new group modal
    this.intercomEventService.trackAnalyticsEvent({
      action: 'rendered',
      object: 'generate_by_topic_cta',
      context: 'playground',
      place: 'new_group_modal',
      is_enabled: hasTopics,
    });
  }

  get disableContinueButton() {
    return (
      this.disableBasedOnManualAdd ||
      this.disableBasedOnCSVUpload ||
      this.disableContinueButtonForTopicSelection ||
      this.disableBasedOnQuestionGenerationNotPossible
    );
  }

  get disableBasedOnCSVUpload() {
    // Disable the continue button if file isn't valid
    // or if the file is valid but there are no questions in it
    return (
      this.questionAdditionMethod === 'csv' &&
      (!this.fileIsValid || this.questionsFromCSV.length === 0)
    );
  }

  get disableBasedOnManualAdd() {
    return (
      this.questionAdditionMethod === 'manual' &&
      (this.manuallyAddedQuestions.length === 0 ||
        !this.manuallyAddedQuestions.some((q) => q.text.trim()))
    );
  }

  get disableBasedOnQuestionGenerationNotPossible() {
    return (
      this.questionAdditionMethod === 'question-generation' &&
      !this.args.isQuestionGenerationPossible
    );
  }

  get defaultGroupNames() {
    if (this.questionAdditionMethod === 'manual') {
      return this.intl.t('ai-agent.playground.default-group-names.manual');
    } else if (this.questionAdditionMethod === 'csv') {
      return this.intl.t('ai-agent.playground.default-group-names.csv');
    } else {
      return this.intl.t('ai-agent.playground.default-group-names.question-generation');
    }
  }

  get showTopicSelection() {
    return (
      this.questionAdditionMethod === 'question-generation' &&
      this.conversationSource === 'by-topic'
    );
  }

  get disableContinueButtonForTopicSelection() {
    return (
      this.questionAdditionMethod === 'question-generation' &&
      this.conversationSource === 'by-topic' &&
      !this.selectedTopic
    );
  }

  get learnMoreLinkHtml(): SafeString {
    return sanitizeHtml(
      `<a href="${TOPICS_EXPLORER_HELP_URL}" target="_blank" class="underline" data-track-learn-more="true">${this.intl.t('ai-agent.playground.generate-dropdown.learn-more')}</a>`,
    );
  }

  get byTopicDisabledTooltip(): string {
    return String(
      this.intl.t('ai-agent.playground.generate-dropdown.by-topic-disabled-tooltip', {
        learnMoreLinkHtml: this.learnMoreLinkHtml,
        htmlSafe: true,
      }),
    );
  }

  @action
  trackLearnMoreClick(event: Event) {
    let target = event.target as HTMLElement;
    if (target.getAttribute('data-track-learn-more') === 'true') {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'learn_more_insights_topics_link',
        context: 'playground',
        place: 'new_group_modal_tooltip',
      });
    }
  }

  @action
  setQuestionAdditionMethod(method: QuestionAdditionMethod) {
    this.questionAdditionMethod = method;

    // Reset conversation source and topic when switching methods
    if (method !== 'question-generation') {
      this.conversationSource = 'all';
      this.selectedTopic = undefined;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'question_addition_method',
      value: method,
      context: 'playground',
    });
  }

  @action
  setConversationSource(source: ConversationSource) {
    this.conversationSource = source;
    // Reset selected topic when switching to 'all'
    if (source === 'all') {
      this.selectedTopic = undefined;
    }
  }

  @action
  updateTopicSelection(topicId?: string) {
    this.selectedTopic = topicId;
  }

  @action updateManuallyAddedQuestions(questions: TrackedArray<QuestionInput>) {
    this.manuallyAddedQuestions = questions;
  }

  @action
  async createGroupAndCreateQuestions() {
    try {
      if (this.questionAdditionMethod === 'manual') {
        await this.createGroup(
          undefined,
          this.manuallyAddedQuestions.map((q) => q.text),
        );
      } else if (this.questionAdditionMethod === 'csv') {
        await this.createGroup(undefined, this.questionsFromCSV);
      } else if (this.questionAdditionMethod === 'question-generation') {
        if (this.conversationSource === 'by-topic') {
          await this.createGroup(ExtractionType.All, undefined, this.selectedTopic);
        } else {
          await this.createGroup(ExtractionType.All);
        }
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.banners.creating-group.question-creation-error'),
      );
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'new_group_continue_button',
      context: 'playground',
      method: this.questionAdditionMethod,
    });
  }

  @action
  async setFile(file: any) {
    this.file = file;
    this.fileIsValid = file !== null;
    this.questionsFromCSV = await this.parseCsvFile();
  }

  @action
  async parseCsvFile(): Promise<string[]> {
    if (!this.file) {
      return [];
    }

    try {
      this.parsingErrorMessage = '';
      let results = await this.playgroundApi.parseCsvFile(this.file);

      if (results.data.length === 0) {
        throw new Error(
          this.intl.t('ai-agent.playground.upload-questions-modal.fallback-processing-error'),
        );
      }

      // For the Alpha release, we only support a single column of questions
      let questions = (results as { data: string[] }).data.map((row: any) => row[0]);
      return questions;
    } catch (error) {
      this.fileFailedToUpload = true;

      if (error instanceof ResponseError) {
        this.parsingErrorMessage = this.intl.t(
          'ai-agent.playground.call-error.upload-questions.message',
        );
      } else {
        this.parsingErrorMessage =
          error.message ||
          this.intl.t('ai-agent.playground.upload-questions-modal.fallback-processing-error');
        this.fileIsValid = false;
      }
      return [];
    }
  }

  generateUniqueGroupName(baseName: string): string {
    let existingGroups = this.playgroundApi.playgroundSummary?.groupSummaries ?? [];
    let existingNames = existingGroups.map((group) => group.name);

    let maxNumber = 0;

    // Check if base name exists (treat as number 1)
    if (existingNames.includes(baseName)) {
      maxNumber = 1;
    }

    // Check for numbered versions
    let baseNamePattern = new RegExp(`^${escapeRegExp(baseName)} \\((\\d+)\\)$`);

    existingNames.forEach((name) => {
      let match = name.match(baseNamePattern);
      if (match) {
        let number = parseInt(match[1], 10);
        if (number > maxNumber) {
          maxNumber = number;
        }
      }
    });

    // First one unnumbered, rest numbered starting from 2
    if (maxNumber === 0) {
      return baseName;
    } else {
      return `${baseName} (${maxNumber + 1})`;
    }
  }

  @action
  async createGroup(extractionType?: ExtractionType, questions?: string[], topicId?: string) {
    let groupName;

    // Priority: 1) User-entered text, 2) Topic name if topic-based, 3) Default names
    if (this.groupName?.trim()) {
      groupName = this.groupName;
    } else if (this.conversationSource === 'by-topic' && this.selectedTopicName) {
      groupName = this.generateUniqueGroupName(this.selectedTopicName);
    } else {
      groupName = this.defaultGroupNames;
    }

    if (questions && questions.length > this.args.questionLimit) {
      this.args.showExceedingQuestionLimitNotification();
      questions = questions.slice(0, this.args.questionLimit);
    }

    // Reverse questions to maintain chronological order within batches
    if (questions && questions.length > 0) {
      questions = [...questions].reverse();
    }

    let createdGroupSummary = await this.playgroundApi.createPlaygroundGroup(
      groupName,
      [],
      undefined,
      extractionType,
      questions,
      topicId,
    );

    let source: string | null = null;
    switch (this.questionAdditionMethod) {
      case 'manual':
        source = 'add_questions_form';
        break;
      case 'csv':
        source = 'csv_upload';
        break;
      case 'question-generation':
        source = 'question_generation';
        break;
      default:
        // Log to Sentry but don't break the user experience
        captureException(
          new Error(`Unhandled questionAdditionMethod: ${this.questionAdditionMethod}`),
          {
            extra: {
              questionAdditionMethod: this.questionAdditionMethod,
              component: 'new-group-modal',
            },
            tags: {
              team: 'team-ai-agent-2',
            },
          },
        );
        break;
    }

    if (source) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'added_questions',
        object: 'playground',
        new_question_count: questions?.length ?? 0,
        source,
        topic_id: topicId,
      });
    }

    this.args.onClose();

    // Navigate to the test route with the new group ID
    // We use currentRouteName to account for user being on the standalone testing route
    this.router.transitionTo(this.router.currentRouteName, createdGroupSummary.id);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::Modals::NewGroupModal::Modal': typeof Modal;
    'ai-agent/playground/modals/new-group-modal/modal': typeof Modal;
  }
}
